import * as React from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { AddOnEditorDialog } from "@/components/admin/AddOnEditorDialog";
import { useAdminAddOns, useDeactivateAddOn } from "@/hooks/useAdmin";
import { useToast } from "@/hooks/use-toast";
import { ApiError } from "@/lib/api";
import type { AddOnDto } from "@/types/api";

export function AdminAddOnsPage() {
  const { data, isLoading } = useAdminAddOns();
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<AddOnDto | undefined>();
  const deactivate = useDeactivateAddOn();
  const { toast } = useToast();

  function openCreate() { setEditing(undefined); setEditorOpen(true); }
  function openEdit(a: AddOnDto) { setEditing(a); setEditorOpen(true); }

  async function onDeactivate(slug: string) {
    try {
      await deactivate.mutateAsync(slug);
      toast({ title: "Add-on deactivated", variant: "success" });
    } catch (err) {
      toast({ title: "Failed", description: err instanceof ApiError ? err.message : "Network error.", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Add-ons</h2>
          <p className="text-sm text-muted-foreground">Catalog shown on /add-ons.</p>
        </div>
        <Button onClick={openCreate}><Plus className="h-4 w-4" />New add-on</Button>
      </div>

      {isLoading ? (
        <Card><CardContent className="space-y-2 py-5">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </CardContent></Card>
      ) : !data || data.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          No add-ons yet. Create one to populate /add-ons.
        </CardContent></Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Add-on</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead>Price</TableHead>
                <TableHead className="hidden md:table-cell">HostShop code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map(a => (
                <TableRow key={a.slug}>
                  <TableCell>
                    <p className="font-medium">{a.name}</p>
                    <p className="font-mono text-xs text-muted-foreground">{a.slug}</p>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {a.scope}
                    {a.is_one_time && <Badge variant="soft" className="ml-2">One-time</Badge>}
                  </TableCell>
                  <TableCell className="text-sm">${Math.round(a.price_monthly)}<span className="text-muted-foreground">{a.is_one_time ? " one-time" : "/mo"}</span></TableCell>
                  <TableCell className="hidden font-mono text-xs text-muted-foreground md:table-cell">{a.hostshop_product_code ?? "—"}</TableCell>
                  <TableCell>
                    {a.active ? <Badge variant="success">Active</Badge>
                              : <Badge variant="outline">Inactive</Badge>}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(a)} aria-label={`Edit ${a.slug}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {a.active && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label={`Deactivate ${a.slug}`}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Deactivate {a.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                It'll disappear from /add-ons. Existing purchases keep working; you can re-activate via Edit.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDeactivate(a.slug)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >Deactivate</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <AddOnEditorDialog open={editorOpen} onOpenChange={setEditorOpen} initial={editing} />
    </div>
  );
}
